/**
 * The feed (spec 6, 9.2, 9.3, build spec 4.3). It owns the pager (`usePager`) and the screen-list
 * reducer; every learning decision comes from the API (ADR 0005).
 *
 * Four rules worth naming:
 *  - forward scrolling is capped because only `furthest + 1` screens exist, not by an event
 *    handler — landing on a new furthest card appends the next screen and sends a
 *    fire-and-forget `PATCH /feed/position`, whose clamp on the server is the truth;
 *  - "landing" means a page turn has finished on a screen. The pager turns exactly one page at
 *    a time, so nothing is ever decided mid-move and no screen is ever swept past;
 *  - the gate and every checkpoint answer are awaited, because they change what the student
 *    may see next (spec 6.1, 7);
 *  - a `chapter_reset` from any of those means the operator withdrew or republished the topic
 *    (spec 6.2), so the feed simply reloads from `GET /feed`.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import { authedJson, authedVoid, reportClientError } from "../../shared/api.js";
import type { AnswerResponse, Chapter, FeedResponse, Question } from "../../shared/api-types.js";
import { ApiClientError, germanMessage } from "../../shared/errors.js";
import { OfflineState, Splash } from "../states.js";
import { CardScreen } from "./CardScreen.js";
import { CheckpointScreen, type Graded } from "./CheckpointScreen.js";
import { GateScreen } from "./GateScreen.js";
import { AllReadScreen, CelebrationScreen, ResultScreen } from "./ResultScreens.js";
import {
  feedReducer,
  initialScrollIndex,
  initialState,
  resultScrollIndex,
  type FeedState,
} from "./screens.js";
import { usePager } from "./usePager.js";

interface FeedProps {
  categoryLabel: string;
  dayGoal: number;
}

type LoadState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "empty" }
  | { status: "ready"; state: FeedState };

export function FeedScreen({ categoryLabel, dayGoal }: FeedProps) {
  const [load, setLoad] = useState<LoadState>({ status: "loading" });
  const [reloadToken, setReloadToken] = useState(0);

  const reload = useCallback(() => {
    setLoad({ status: "loading" });
    setReloadToken((token) => token + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const feed = await authedJson<FeedResponse>("/feed");
        if (cancelled) return;
        if (!feed.chapter) {
          setLoad({ status: "empty" });
          return;
        }
        setLoad({ status: "ready", state: initialState(feed.chapter, feed.today) });
      } catch (err) {
        if (cancelled) return;
        setLoad({ status: "error", message: germanMessage(err) });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reloadToken]);

  if (load.status === "loading") return <Splash />;
  if (load.status === "error") return <OfflineState onRetry={reload} message={load.message} />;
  if (load.status === "empty") {
    return (
      <OfflineState
        onRetry={reload}
        title="Noch keine Themen"
        icon="📚"
        message="Für diese Klasse gibt es gerade keine Themen. Sag es deiner Lehrerin oder deinem Lehrer."
      />
    );
  }

  return (
    <LoadedFeed
      key={load.state.chapter.topic.id + ":" + reloadToken}
      initial={load.state}
      categoryLabel={categoryLabel}
      dayGoal={dayGoal}
      onReload={reload}
    />
  );
}

function LoadedFeed({
  initial,
  categoryLabel,
  dayGoal,
  onReload,
}: FeedProps & {
  initial: FeedState;
  onReload: () => void;
}) {
  const [state, dispatch] = useReducer(feedReducer, initial);
  const [creditOpen, setCreditOpen] = useState(false);
  const [gatePending, setGatePending] = useState(false);
  const [gateError, setGateError] = useState<string | null>(null);
  const enteredNext = useRef(false);
  const pager = usePager({
    count: state.screens.length,
    initialIndex: initialScrollIndex(initial),
    onRest: handleRest,
  });
  const { moveTo } = pager;

  // Build spec 4.3: landing on the last existing screen extends the list by one and tells the
  // server where the student is. The PATCH is fire-and-forget; only `chapter_reset` matters.
  const sendPosition = useCallback(
    (card: number) => {
      void authedJson<{ currentCard: number; furthestCard: number }>("/feed/position", {
        method: "PATCH",
        body: JSON.stringify({ currentCard: card }),
      }).catch((err: unknown) => {
        if (err instanceof ApiClientError && err.code === "chapter_reset") onReload();
        // Any other failure costs at most one card of resume position; the server clamps.
      });
    },
    [onReload],
  );

  /** What happens when a page turn finishes on screen `index`. */
  function handleRest(index: number) {
    const screen = state.screens[index];
    if (!screen) return;
    if (screen.kind === "card") {
      sendPosition(screen.position);
      if (index === state.screens.length - 1) dispatch({ type: "advance" });
    }
    if (screen.kind === "gate") sendPosition(state.chapter.cards.length + 1);
    if (screen.kind === "nextCard" && !enteredNext.current) {
      // Swapping chapters drops every earlier screen, so a finished topic is gone. The new
      // chapter's first card looks exactly like the cover it replaces, so the jump is unseen.
      enteredNext.current = true;
      dispatch({ type: "enterNextChapter" });
      moveTo(0, "jump");
    }
  }

  useEffect(() => {
    if (state.result) enteredNext.current = false;
  }, [state.result]);

  async function confirmGate() {
    setGatePending(true);
    setGateError(null);
    try {
      // The gate screen's own position update is fire-and-forget, so it can still be in flight
      // when the student taps — and the server refuses to confirm a gate it does not believe
      // was reached (spec 6.1). Await the position first: one extra round trip on a single
      // deliberate tap, instead of a 409 whenever the tap beats the PATCH.
      await authedJson<{ currentCard: number; furthestCard: number }>("/feed/position", {
        method: "PATCH",
        body: JSON.stringify({ currentCard: state.chapter.cards.length + 1 }),
      });
      await authedVoid("/feed/gate", { method: "POST" });
      dispatch({ type: "gateConfirmed" });
      moveTo(0, "jump");
    } catch (err) {
      if (err instanceof ApiClientError && err.code === "chapter_reset") {
        onReload();
        return;
      }
      setGateError(germanMessage(err));
    } finally {
      setGatePending(false);
    }
  }

  const answer = useCallback(async (question: Question, optionId: string): Promise<Graded> => {
    const response = await authedJson<AnswerResponse>("/feed/answer", {
      method: "POST",
      body: JSON.stringify({ questionId: question.id, optionId }),
    });
    if (response.result) {
      const result = response.result;
      // Let the deck finish its feedback and slide-out before the result screen appears.
      setTimeout(() => {
        dispatch({ type: "result", result });
      }, 1250);
    }
    return {
      chosenOptionId: optionId,
      correctOptionId: response.correctOptionId,
      isCorrect: response.isCorrect,
    };
  }, []);

  // Scroll to the result once the reducer has added it.
  const sawResult = useRef(false);
  useEffect(() => {
    if (!state.result || sawResult.current) return;
    sawResult.current = true;
    moveTo(resultScrollIndex(state), "animate");
  }, [state, moveTo]);
  useEffect(() => {
    if (!state.result) sawResult.current = false;
  }, [state.result]);

  // Build spec 4.3: the current image and the next one load eagerly, the rest lazily.
  const eagerPositions = useMemo(() => {
    const screen = state.screens[pager.index];
    const current = screen?.kind === "card" ? screen.position : 1;
    return new Set([current, current + 1]);
  }, [state.screens, pager.index]);

  const chapter: Chapter = state.chapter;
  const questionCount = chapter.checkpoint.questions.length;

  return (
    <div ref={pager.viewport} className={"feed-viewport" + (pager.moving ? " is-moving" : "")}>
      {state.screens.map((screen, index) => {
        const key = screen.kind + ":" + (screen.kind === "card" ? screen.position : index);
        return (
          <div
            key={key}
            className="feed-slot"
            data-index={index}
            // Where this screen sits relative to the current one, in screen heights.
            style={{ "--pos": index - pager.index } as CSSProperties}
          >
            {screen.kind === "card" && renderCard(screen.position)}
            {screen.kind === "gate" && (
              <GateScreen
                chapter={chapter}
                categoryLabel={categoryLabel}
                today={state.today}
                dayGoal={dayGoal}
                questionCount={questionCount}
                pending={gatePending}
                error={gateError}
                onConfirm={() => void confirmGate()}
              />
            )}
            {screen.kind === "checkpoint" && (
              <CheckpointScreen
                chapter={chapter}
                categoryLabel={categoryLabel}
                today={state.today}
                dayGoal={dayGoal}
                onAnswer={answer}
              />
            )}
            {screen.kind === "result" && state.result && (
              <ResultScreen result={state.result} dayGoal={dayGoal} />
            )}
            {screen.kind === "celebration" && <CelebrationScreen dayGoal={dayGoal} />}
            {screen.kind === "allRead" && <AllReadScreen />}
            {screen.kind === "nextCard" && state.nextChapter && renderNextCover(state.nextChapter)}
            {screen.kind === "noMore" && (
              <OfflineState
                onRetry={onReload}
                title="Kein neues Thema"
                icon="📚"
                message="Gerade gibt es kein neues Thema. Versuche es später nochmal."
              />
            )}
          </div>
        );
      })}
    </div>
  );

  function renderCard(position: number) {
    const card = chapter.cards.find((candidate) => candidate.position === position);
    if (!card) return null;
    return (
      <CardScreen
        card={card}
        totalCards={chapter.cards.length}
        categoryLabel={categoryLabel}
        topicTitle={chapter.topic.title}
        today={state.today}
        dayGoal={dayGoal}
        credit={chapter.credit}
        creditOpen={creditOpen}
        onOpenCredit={() => setCreditOpen(true)}
        onCloseCredit={() => setCreditOpen(false)}
        eager={eagerPositions.has(position)}
      />
    );
  }

  function renderNextCover(next: Chapter) {
    const card = next.cards[0];
    if (!card) return null;
    return (
      <CardScreen
        card={card}
        totalCards={next.cards.length}
        categoryLabel={categoryLabel}
        topicTitle={next.topic.title}
        today={state.today}
        dayGoal={dayGoal}
        credit={next.credit}
        creditOpen={false}
        onOpenCredit={() => undefined}
        onCloseCredit={() => undefined}
        eager
      />
    );
  }
}

/** ADR 0011: unhandled render errors go to our own endpoint, never to a third-party reporter. */
export function reportFeedError(error: unknown): void {
  if (error instanceof Error) reportClientError(error.message, error.stack);
  else reportClientError(String(error));
}
